// src/components/TrialWarningLiveDemo.js
import React, { useState, createContext, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Grid,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrialWarningBanner from './TrialWarningBanner';

// Mock trial states for demo
const mockTrialStates = {
  'loading': {
    loading: true,
    isTrialing: false,
    daysRemaining: null,
    trialEndDate: null,
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'active',
    error: null
  },
  'active_14_days': {
    loading: false,
    isTrialing: true,
    daysRemaining: 14,
    trialEndDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'trialing',
    error: null
  },
  'active_7_days': {
    loading: false,
    isTrialing: true,
    daysRemaining: 7,
    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'trialing',
    error: null
  },
  'warning_3_days': {
    loading: false,
    isTrialing: true,
    daysRemaining: 3,
    trialEndDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'trialing',
    error: null
  },
  'critical_1_day': {
    loading: false,
    isTrialing: true,
    daysRemaining: 1,
    trialEndDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000),
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'trialing',
    error: null
  },
  'expired_today': {
    loading: false,
    isTrialing: false,
    daysRemaining: 0,
    trialEndDate: new Date(),
    isExpired: true,
    isRestricted: true,
    subscriptionStatus: 'expired',
    error: null
  },
  'expired_restricted': {
    loading: false,
    isTrialing: false,
    daysRemaining: 0,
    trialEndDate: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    isExpired: true,
    isRestricted: true,
    subscriptionStatus: 'expired',
    error: null
  },
  'paid_active': {
    loading: false,
    isTrialing: false,
    daysRemaining: null,
    trialEndDate: null,
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'active',
    error: null
  },
  'api_error': {
    loading: false,
    isTrialing: true,
    daysRemaining: null,
    trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    isExpired: false,
    isRestricted: false,
    subscriptionStatus: 'trialing',
    error: 'Unable to connect to trial service'
  }
};

// Context for demo mode
const TrialDemoContext = createContext();

// Provider component
export const TrialDemoProvider = ({ children }) => {
  const [demoMode, setDemoMode] = useState(false);
  const [selectedState, setSelectedState] = useState('warning_3_days');
  
  return (
    <TrialDemoContext.Provider value={{
      demoMode,
      setDemoMode,
      selectedState,
      setSelectedState,
      mockTrialStates
    }}>
      {children}
    </TrialDemoContext.Provider>
  );
};

// Hook to use demo context
export const useTrialDemo = () => {
  const context = useContext(TrialDemoContext);
  if (!context) {
    return {
      demoMode: false,
      selectedState: 'active_14_days',
      mockTrialStates: {},
      setDemoMode: () => {},
      setSelectedState: () => {}
    };
  }
  return context;
};

const TrialWarningLiveDemo = () => {
  const navigate = useNavigate();
  const [showInstructions, setShowInstructions] = useState(true);
  const { demoMode, setDemoMode, selectedState, setSelectedState } = useTrialDemo();

  const stateDescriptions = {
    'loading': { label: 'Loading State', desc: 'Initial load state', color: 'default' },
    'active_14_days': { label: 'New Trial (14 days)', desc: 'Fresh trial, info banner', color: 'info' },
    'active_7_days': { label: 'Mid Trial (7 days)', desc: 'Mid-way through trial', color: 'info' },
    'warning_3_days': { label: 'Warning (3 days)', desc: 'Warning banner appears', color: 'warning' },
    'critical_1_day': { label: 'Critical (1 day)', desc: 'Urgent warning', color: 'error' },
    'expired_today': { label: 'Expired Today', desc: 'Just expired', color: 'error' },
    'expired_restricted': { label: 'Expired & Restricted', desc: 'Account limited', color: 'error' },
    'paid_active': { label: 'Paid Subscriber', desc: 'No banner shown', color: 'success' },
    'api_error': { label: 'API Error', desc: 'Fallback with error', color: 'warning' }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
        🎛️ Trial Warning Banner - Live Demo
      </Typography>

      {showInstructions && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          onClose={() => setShowInstructions(false)}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>How to use this demo:</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            1. Toggle "Demo Mode" to override the real trial status<br/>
            2. Select different trial states to preview the banner<br/>
            3. The banner will appear at the top of this page (and all authenticated pages)<br/>
            4. Navigate to other pages to see the banner in different contexts
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Demo Controls
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={demoMode}
                    onChange={(e) => setDemoMode(e.target.checked)}
                    color="primary"
                  />
                }
                label="Enable Demo Mode"
                sx={{ mb: 2, display: 'block' }}
              />

              {demoMode && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Trial State</InputLabel>
                  <Select
                    value={selectedState}
                    label="Trial State"
                    onChange={(e) => setSelectedState(e.target.value)}
                  >
                    {Object.entries(stateDescriptions).map(([key, info]) => (
                      <MenuItem key={key} value={key}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Chip size="small" label={info.label} color={info.color} />
                          <Typography variant="body2" color="text.secondary">
                            {info.desc}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                <strong>Current Mode:</strong> {demoMode ? 'Demo' : 'Live API'}
                {demoMode && (
                  <>
                    <br/>
                    <strong>Selected State:</strong> {stateDescriptions[selectedState]?.label}
                  </>
                )}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button 
                  size="small" 
                  onClick={() => navigate('/dashboard')}
                  variant="outlined"
                >
                  View on Dashboard
                </Button>
                <Button 
                  size="small" 
                  onClick={() => navigate('/clients')}
                  variant="outlined"
                >
                  View on Clients
                </Button>
                <Button 
                  size="small" 
                  onClick={() => navigate('/settings')}
                  variant="outlined"
                >
                  View on Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Banner Preview
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This shows how the banner appears at the top of authenticated pages:
              </Typography>
              
              {/* Simulated layout preview */}
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 0,
                  backgroundColor: '#fafafa',
                  border: '2px dashed #ccc'
                }}
              >
                {/* Simulated app bar */}
                <Box sx={{ 
                  backgroundColor: '#1976d2', 
                  color: 'white', 
                  p: 2,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="h6">
                    MedInsight Dashboard
                  </Typography>
                  <Box sx={{ ml: 'auto' }}>
                    <Button color="inherit" size="small">Logout</Button>
                  </Box>
                </Box>

                {/* This is where the actual banner will appear */}
                <TrialWarningBanner />

                {/* Simulated page content */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    Dashboard Content
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This represents the main page content. The trial warning banner appears above this content on all authenticated pages.
                  </Typography>
                </Box>
              </Paper>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary">
                <strong>Banner Logic:</strong> {
                  !demoMode 
                    ? 'Using live API data from your actual trial status'
                    : selectedState === 'paid_active'
                    ? 'No banner shown for active paid subscribers'
                    : selectedState === 'loading'
                    ? 'Loading state - no banner shown'
                    : selectedState.includes('expired')
                    ? 'Critical error banner - cannot be dismissed, blocks user workflow'
                    : selectedState.includes('critical') || selectedState.includes('warning')
                    ? 'Warning banner - can be dismissed but will reappear on page reload'
                    : 'Info banner showing trial status with progress indicator'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>✅ Implementation Status:</strong> The trial warning system is fully integrated. 
          When demo mode is OFF, users see banners based on their real trial status from the backend API.
          The banner automatically updates every 30 minutes and responds to actual trial state changes.
        </Typography>
      </Alert>
    </Box>
  );
};

export default TrialWarningLiveDemo;